(ns common.queries.http
  (:refer-clojure :exclude [get])
  (:require [cljs-bean.core :refer [->js]]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.db.config :refer [get-config]]
            [common.db.effects.http :refer [parse-response*]]
            [common.url-template :as url-template]
            [promesa.core :as p]))

(def config (get-config))

(deftype HttpClient [auth-token static-vars])

;; Global state to hold the auth-token etc. for http calls.
;; Defonce so on shadow-cljs reload requests don't trigger without auth-token at first.
(defonce http-client (HttpClient. nil {:api-url (:apiUrl config)}))

(defn set-auth-token! [token]
  (set! (.-auth-token http-client) token))

(defn get-auth-token
  []
  (.-auth-token http-client))

;; Static parts
;; :api-url
;;
;; Dynamic parts
;; :agency-id
;; :advertiser-id
;; :adset-id
;; ... all other parameters

(def default-headers
  {:Accept "application/json"})

(def with-body-headers
  {:Content-Type "application/json"})

(defn build-request [options]
  (let [body (:body options)
        format (:format options :json)
        ;; TODO: Add :response-format option and use it to setup
        ;; Accept header.
        req (merge-with merge
                        {:headers (merge default-headers
                                         (when (not (:skip-auth-token options))
                                           {:Authorization (get-auth-token)}))}
                        (dissoc options :body :skip-auth-token :format))
        req (if (some? body)
              (case format
                :json (-> (merge-with merge req {:headers with-body-headers})
                          (assoc :body (js/JSON.stringify (->js body))))
                :form-data (assoc req :body body))
              req)]
    req))

(defn response-map [fetch-resp resp-data headers]
  {:data resp-data
   :status (.-status fetch-resp)
   :headers headers})

;; TODO: What about cancelling the requests? AbortSignal?

(defn headers->clj [resp]
  (into {} (map (fn [x] [(keyword (aget x 0)) (aget x 1)]) (seq (.entries (.-headers resp))))))

(defn fetch
  "Returns a promise by default (on success) resolving to the
  response body, as a JS object.

  Use :skip-auth-token true to remove the default auth-token header.

  HTTP errors (anything non 200-299) will reject the response
  with a response map."
  ([url]
   (fetch url nil))
  ([url options]
   (let [req (build-request options)
         auth-token (get-in req [:headers :Authorization])
         skip-auth? (:skip-auth-token options)]
     (if (or auth-token skip-auth?)
       (p/let [response-format (:response-format options :json)
               fetch-resp (js/fetch url (->js req))
               headers (headers->clj fetch-resp)
               status (.-status fetch-resp)
               ;; Avoid reading JSON when having 204 No Content status code or
               ;; empty body according to the Content-Length header.
               ;; Json method would throw an exception here.
               empty-body (and (or (= 204 status)
                                   (= 0 (js/parseInt (:content-length headers))))
                               (not= response-format :upload-part))
               resp-data (cond
                           empty-body nil
                           (= status 401) (parse-response* :text fetch-resp)
                           :else (parse-response* response-format fetch-resp))]
         (if (.-ok fetch-resp)
           resp-data
           (p/rejected (response-map fetch-resp resp-data headers))))
       (do
         (js/console.warn "qhttp/fetch blocked — missing auth token and skip-auth-token not set" url)
         (p/rejected {:error "Request blocked: missing auth token and skip-auth-token not set"}))))))

(defn get
  ([url]
   (get url nil))
  ([url options]
   (fetch url (assoc options :method "GET"))))

(defn delete
  ([url]
   (delete url nil))
  ([url options]
   (fetch url (assoc options :method "DELETE"))))

(defn post
  ([url]
   (post url nil))
  ([url options]
   (fetch url (assoc options :method "POST"))))

(defn put
  ([url]
   (put url nil))
  ([url options]
   (fetch url (assoc options :method "PUT"))))

(defn patch
  ([url]
   (patch url nil))
  ([url options]
   (fetch url (assoc options :method "PATCH"))))

;;
;; URL templates
;;

(comment
  ;; WIP a version of url-template render which does big part of the work of parsing
  ;; the template before render call.
  ;; This makes it much simpler to check if all the requires paramters are given etc.
  (def static-vars #{:api-url})

  (def templates
    (->> (for [[k template] url-template/templates
               :when (#{"api" "measure-api"} (namespace k))
               :let [tpl-vars (->> (re-seq url-template/placeholder-re template)
                                   (map (fn [s]
                                          [s (keyword (subs s 1))])))
                     var-keys (set (map second tpl-vars))]]
           [k {;; :static-vars (set/intersection static-vars var-keys)
               :required-vars (set/difference var-keys static-vars)
               :tpl-vars tpl-vars
               :template template}])
         (into {})))

  (defn render [k vars]
    (let [{:keys [template required-vars tpl-vars]} (clojure.core/get templates k)
          missing-vars (set/difference required-vars (set (keys vars)))
          _ (when (seq missing-vars)
              (throw (js/Error. (str "missing vars " missing-vars))))

          ;; TODO: if the static-vars are first, they require less/no special handling
          vars (merge (.-static-vars http-client) vars)
          replace-map (into {} (map (fn [[var-s var-k]]
                                      [var-s (clojure.core/get vars var-k)])
                                    tpl-vars))]
      (str/replace template url-template/placeholder-re replace-map))))

;; NOTE: Consider better name?
(defn render
  "Merges static url parts (e.g. the api-urls) from the http client"
  ([template data]
   (render template data {}))
  ([template data params]
   (url-template/render template
                        (merge (.-static-vars http-client) data)
                        params)))

(defn dispatcher-url [action params]
  (url-template/render :api/dispatcher
                       (merge (.-static-vars http-client) {:action-ns (namespace action)
                                                           :action-name (name action)})
                       params))
